<template>
	<div class="survey-form-classic">
		<v-skeleton-loader
			class="survey-form-classic__form"
			v-if="isLoadingSurvey"
			v-bind="attrs"
			type="card"
		/>
		<div v-else-if="!surveyError" class="survey-form-classic__form">
			<div
				class="survey-form-classic__block"
				:class="{ 'has-error': !!errors[block.survey_question_id] }"
				:style="blockStyle"
				v-for="(block, index) in surveyQuestions"
				:key="block.survey_question_id"
			>
				<h3
					class="survey-form-classic__title"
					:aria-invalid="!!errors[block.survey_question_id]"
					:style="{ justifyContent: surveyTypeQuiz ? 'flex-start' : 'center' }"
				>
					<span v-if="surveyTypeQuiz">{{ `${index + 1}. ` }}</span>
					{{ block.survey_question_text }}
					<span
						v-if="block.survey_question_required"
						class="survey-form-classic__title__required-asterisk"
						>*</span
					>
				</h3>

				<!-- single-select -->
				<div v-if="block.survey_question_type === 'single-select'">
					<div
						v-for="option in block.survey_question_options"
						:key="option.survey_q_option_id"
					>
						<div
							:class="[
								'survey-form-classic__radio',
								isSelected(block, option.survey_q_option_id)
									? 'survey-form-classic__radio__selected'
									: '',
								isCustomOption(option) &&
								isSelected(block, option.survey_q_option_id)
									? 'custom-selected'
									: '',
							]"
							:style="{ backgroundColor: surveyTypeQuiz ? '' : '#ffffff' }"
						>
							<CustomRadio
								:isChecked="isSelected(block, option.survey_q_option_id)"
								:value="option.survey_q_option_id"
								:onChange="
									() =>
										selectSingle(
											block.survey_question_id,
											option.survey_q_option_id
										)
								"
							/>
							<label
								class="survey-form-classic__label"
								:for="`b${block.survey_question_id}-o${option.survey_q_option_id}`"
							>
								{{ option.survey_q_text }}
							</label>
							<div
								v-if="
									isCustomOption(option) &&
									isSelected(block, option.survey_q_option_id)
								"
								class="survey-form-classic__custom-answer survey-form-classic__custom-answer--inside"
							>
								<textarea
									:style="{ backgroundColor: surveyTypeQuiz ? '' : '#ffffff' }"
									class="survey-form-classic__custom-answer__input"
									v-model.trim="answers[block.survey_question_id].custom"
									placeholder="Enter your own answer..."
									rows="1"
									@input="autoResize($event)"
								></textarea>
							</div>
						</div>
					</div>
				</div>

				<!-- multiple-select -->
				<div v-else-if="block.survey_question_type === 'multiple-select'">
					<div class="survey-form-classic__grid">
						<template v-for="(option, idx) in block.survey_question_options">
							<div
								:key="option.survey_q_option_id"
								:style="{ backgroundColor: surveyTypeQuiz ? '#f3f3f6' : '#ffffff' }"
								:class="[
									'survey-form-classic__grid-item',
									isSelected(block, option.survey_q_option_id)
										? 'survey-form-classic__checkbox__selected'
										: '',
									isLastOdd(idx, block.survey_question_options.length)
										? 'full-span'
										: '',
									isCustomOption(option) && isCustomSelected(block, option)
										? 'custom-selected'
										: '',
								]"
							>
								<CustomCheckbox
									:isChecked="isSelected(block, option.survey_q_option_id)"
									:value="`b${block.survey_question_id}-o${option.survey_q_option_id}`"
									:onChange="
										() =>
											toggleMulti(
												block.survey_question_id,
												option.survey_q_option_id
											)
									"
								/>
								<label
									class="survey-form-classic__label ml-2"
									:for="`b${block.survey_question_id}-o${option.survey_q_option_id}`"
								>
									{{ option.survey_q_text }}
								</label>

								<!-- textarea grid-item -->
								<div
									v-if="isCustomOption(option) && isCustomSelected(block, option)"
									class="survey-form-classic__custom-answer survey-form-classic__custom-answer--inside"
									:style="{
										backgroundColor: surveyTypeQuiz ? '#f3f3f6' : '#ffffff',
									}"
								>
									<textarea
										:style="{
											backgroundColor: surveyTypeQuiz ? '#f3f3f6' : '#ffffff',
										}"
										class="survey-form-classic__custom-answer__input"
										v-model.trim="answers[block.survey_question_id].custom"
										placeholder="Enter your own answer..."
										rows="1"
										@input="autoResize($event)"
									></textarea>
								</div>
							</div>
						</template>
					</div>
				</div>

				<!-- text-box -->
				<div v-else-if="block.survey_question_type === 'text-box'">
					<div
						v-if="answers[block.survey_question_id]"
						:style="{
							backgroundColor: surveyTypeQuiz ? '#f3f3f6' : '#ffffff',
							borderRadius: surveyTypeQuiz ? '' : '8px',
						}"
						class="survey-form-classic__custom-answer survey-form-classic__custom-answer--text-box survey-form__grid-item full-span"
					>
						<textarea
							:style="{
								backgroundColor: surveyTypeQuiz ? '' : '#ffffff',
							}"
							class="survey-form-classic__custom-answer__input"
							v-model.trim="answers[block.survey_question_id].custom"
							placeholder="Enter your own answer..."
							@input="autoResize($event)"
						></textarea>
					</div>
				</div>

				<!-- rating -->
				<div v-else-if="block.survey_question_type === 'rating'">
					<SurveyRating
						:surveyTypeQuiz="surveyTypeQuiz"
						v-model="answers[block.survey_question_id].selected"
					/>
				</div>
			</div>
		</div>
		<div class="survey-form-classic__button-wrapper" v-if="!surveyError">
			<button
				:disabled="currentStep === 1"
				v-if="!surveyTypeQuiz"
				@click="backStepToggle"
				:class="
					currentStep === 1
						? 'survey-form-classic__back-button__enable'
						: 'survey-form-classic__back-button'
				"
				class="survey-form-classic__back-button"
				type="button"
			>
				<span>Back</span>
			</button>
			<button
				type="button"
				class="survey-form-classic__submit-button"
				:class="
					isSubmitEnabled
						? 'survey-form-classic__submit-button--enabled'
						: 'survey-form-classic__submit-button--disabled'
				"
				:disabled="!isSubmitEnabled"
				@click="onSubmit"
			>
				<v-progress-circular v-if="postSurveyLoading"></v-progress-circular>
				<span v-else>
					{{ buttonSubmitTitle }}
				</span>
			</button>
		</div>
		<InfoModal
			:error="surveyError ? surveyError.message : ''"
			:isModal="isModal"
			@closeModal="closeModal"
		/>
	</div>
</template>

<script>
import CustomCheckbox from '@/components/CustomCheckbox.vue'
import CustomRadio from '@/components/CustomRadio.vue'
import SurveyRating from '@/components/Event/SurveyRating.vue'
import InfoModal from '@/components/Modals/InfoModal.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
	components: { CustomCheckbox, CustomRadio, SurveyRating, InfoModal },
	props: {
		surveyQuestions: { type: Array, default: () => [] },
		surveyId: { type: Number, default: 0 },
		surveyTypeQuiz: { type: Boolean, default: false },
		totalSteps: { type: Number, default: 0 },
		currentStep: { type: Number, default: 0 },
	},
	data() {
		return {
			attrs: {
				elevation: 0,
				class: 'pt-7',
			},
			isModal: false,
			answers: {},
			rating: 0,
			result: [],
			errors: {},
		}
	},
	watch: {
		surveyQuestions: {
			immediate: true,
			handler(newVal) {
				if (Array.isArray(newVal) && newVal.length > 0) this.initAnswers()
			},
		},
		surveyError: {
			immediate: true,
			handler(newVal) {
				if (newVal?.response) {
					const status = newVal.response.status
					if (status === 404) {
						newVal.message = 'Survey not found'
					}
					if (status === 422) {
						newVal.message = 'Your submission has already been collected.'
					}
					this.isModal = true
				}
			},
		},
	},
	computed: {
		...mapGetters('event', ['survey', 'isLoadingSurvey', 'surveyError', 'postSurveyLoading']),
		buttonSubmitTitle() {
			if (this.surveyTypeQuiz || this.currentStep === this.totalSteps) {
				return 'Submit'
			} else {
				return 'Next'
			}
		},
		blockStyle() {
			if (this.surveyTypeQuiz) {
				return {
					padding: '12px',
					border: '',
					backgroundColor: '#ffffff',
				}
			} else {
				return {
					padding: '0',
					border: 'unset',
					backgroundColor: 'unset',
				}
			}
		},
		isSubmitEnabled() {
			return this.surveyQuestions.every((block) => {
				if (!block.survey_question_required) return true
				const a = this.answers[block.survey_question_id]
				return this.isBlockAnswered(block, a)
			})
		},
	},
	mounted() {
		if (
			Array.isArray(this.surveyQuestions) &&
			this.surveyQuestions.length &&
			!Object.keys(this.answers).length
		) {
			this.initAnswers()
		}
	},
	methods: {
		...mapActions('event', ['getEventSurvey', 'postSurvey', 'postNextStep', 'postBackStep']),
		deriveInitialAnswer(block) {
			const opts = Array.isArray(block.survey_question_options)
				? block.survey_question_options
				: []

			const selectedOpts = opts.filter((o) => o.is_selected)

			switch (block.survey_question_type) {
				case 'rating': {
					const opt =
						selectedOpts[0] || opts.find((o) => o.survey_q_option_type === 'rating')
					const ratingValue = opt?.value_int ?? 0
					return { selected: Number(ratingValue) || 0, custom: '', customSelected: false }
				}

				case 'single-select': {
					const opt = selectedOpts[0]
					const selected = opt ? opt.survey_q_option_id : null
					const custom =
						opt && opt.survey_q_option_type === 'custom'
							? (opt.value_text || '').trim()
							: ''
					return {
						selected,
						custom,
						customSelected: !!(opt && opt.survey_q_option_type === 'custom'),
					}
				}

				case 'multiple-select': {
					const selected = selectedOpts.map((o) => o.survey_q_option_id)
					const customOpt = selectedOpts.find((o) => o.survey_q_option_type === 'custom')
					const custom = customOpt?.value_text ? customOpt.value_text.trim() : ''
					return { selected, custom, customSelected: !!customOpt }
				}

				case 'text-box': {
					const opt = selectedOpts[0] || opts[0]
					const custom = (opt?.value_text || '').trim()
					return { selected: null, custom, customSelected: !!custom }
				}

				default:
					return { selected: null, custom: '', customSelected: false }
			}
		},
		getAnswer(qid) {
			if (!this.answers[qid]) {
				this.$set(this.answers, qid, { selected: null, custom: '', customSelected: false })
			}
			return this.answers[qid]
		},
		markError(qid) {
			this.$set(this.errors, qid, true)
		},
		clearError(qid) {
			if (this.errors[qid]) this.$delete(this.errors, qid)
		},
		isSelected(block, optionId) {
			const a = this.answers[block.survey_question_id]
			return block.survey_question_type === 'multiple-select'
				? Array.isArray(a?.selected) && a.selected.includes(optionId)
				: a?.selected === optionId
		},

		closeModal() {
			this.isModal = false
			const event = this.$route.query.event_id
			const session = this.$route.query.event_agenda_id
			if (session) {
				return this.$router.push({
					name: 'event-session-by-id',
					params: { id: session },
				})
			} else {
				return this.$router.push({
					name: 'event-by-id',
					params: { id: event },
				})
			}
		},

		isBlockAnswered(block, answer) {
			if (!answer) return false
			const type = block.survey_question_type
			const hasText = !!(answer.custom && answer.custom.trim())

			if (type === 'single-select') {
				if (!answer.selected) return false
				const opt = this.findOption(block, answer.selected)
				return this.isCustomOption(opt) ? hasText : true
			}

			if (type === 'multiple-select') {
				if (!Array.isArray(answer.selected) || answer.selected.length === 0) return false
				const anyCustomSelected = block.survey_question_options?.some(
					(o) =>
						o.survey_q_option_type === 'custom' &&
						answer.selected.includes(o.survey_q_option_id)
				)
				return anyCustomSelected ? hasText : true
			}

			if (type === 'text-box') {
				return hasText
			}

			if (type === 'rating') {
				return !!(answer.selected && answer.selected !== 0)
			}

			return false
		},

		isCustomSelected(block, option) {
			const a = this.answers[block.survey_question_id]
			if (!a) return false
			return block.survey_question_type === 'multiple-select'
				? Array.isArray(a.selected) && a.selected.includes(option.survey_q_option_id)
				: a.selected === option.survey_q_option_id
		},
		isCustomOption(option) {
			return option && option.survey_q_option_type === 'custom'
		},
		findOption(block, optionId) {
			return (
				block?.survey_question_options?.find((o) => o.survey_q_option_id === optionId) ||
				null
			)
		},
		firstOptionId(block) {
			return block?.survey_question_options?.[0]?.survey_q_option_id ?? null
		},
		async backStepToggle() {
			const form = this.buildFormData()
			await this.postBackStep({ id: this.surveyId, data: form })
			this.$emit('getEventSurvey')
		},

		async onSubmit() {
			this.errors = {}
			let isValid = true

			this.surveyQuestions.forEach((block) => {
				const qid = block.survey_question_id
				const answer = this.answers[qid]
				if (block.survey_question_required) {
					if (!this.isBlockAnswered(block, answer)) {
						this.markError(qid)
						isValid = false
					} else {
						this.clearError(qid)
					}
				}
			})

			if (!isValid) {
				window.scrollTo({ top: 0, behavior: 'smooth' })
				return
			}

			const form = this.buildFormData()
			if (!this.surveyTypeQuiz && this.currentStep !== this.totalSteps) {
				await this.postNextStep({ id: this.surveyId, data: form })
				this.$emit('getEventSurvey')
			} else {
				await this.postSurvey({
					id: this.surveyId,
					data: form,
					isQuiz: !this.surveyTypeQuiz,
				})
				this.isModal = true
			}
		},

		buildFormData() {
			const fd = new FormData()

			this.surveyQuestions.forEach((block) => {
				const qid = block.survey_question_id
				const a = this.answers[qid]
				if (!a) return

				const t = block.survey_question_type
				const customText = (a.custom || '').trim()
				const appendAnswer = (optionIdOrNull, value) => {
					if (optionIdOrNull != null) {
						fd.append(`answers[${qid}][${optionIdOrNull}]`, String(value))
					} else {
						fd.append(`answers[${qid}]`, String(value))
					}
				}

				if (t === 'single-select') {
					if (a.selected != null) {
						const opt = this.findOption(block, a.selected)
						if (this.isCustomOption(opt) && customText) {
							appendAnswer(a.selected, customText)
						} else {
							appendAnswer(a.selected, 1)
						}
					}
				}

				if (t === 'multiple-select') {
					if (Array.isArray(a.selected) && a.selected.length) {
						a.selected.forEach((optId) => {
							const opt = this.findOption(block, optId)
							if (this.isCustomOption(opt) && customText) {
								appendAnswer(optId, customText)
							} else {
								appendAnswer(optId, 1)
							}
						})
					}
				}

				if (t === 'text-box') {
					if (customText) {
						const optId = this.firstOptionId(block)
						if (optId != null) appendAnswer(optId, customText)
					}
				}

				if (t === 'rating') {
					const ratingValue = a.selected
					if (ratingValue != null && ratingValue !== 0) {
						const ratingOptId = this.firstOptionId(block)
						if (ratingOptId != null) appendAnswer(ratingOptId, ratingValue)
					}
				}
			})

			return fd
		},

		toggleMulti(blockId, optionId) {
			const ans = this.getAnswer(blockId)
			const cur = Array.isArray(ans.selected) ? ans.selected.slice() : []
			const i = cur.indexOf(optionId)
			if (i === -1) cur.push(optionId)
			else cur.splice(i, 1)
			this.$set(ans, 'selected', cur)

			const block = this.surveyQuestions.find((b) => b.survey_question_id === blockId)
			const isCustomOption = !!block?.survey_question_options?.some(
				(o) => o.survey_q_option_id === optionId && o.survey_q_option_type === 'custom'
			)
			if (isCustomOption && !cur.includes(optionId)) ans.custom = ''
		},

		selectSingle(blockId, optionId) {
			const ans = this.getAnswer(blockId)
			ans.selected = optionId

			const block = this.surveyQuestions.find((b) => b.survey_question_id === blockId)
			const isCustom = !!block?.survey_question_options?.some(
				(o) => o.survey_q_option_id === optionId && o.survey_q_option_type === 'custom'
			)
			if (!isCustom) ans.custom = ''
		},

		initAnswers() {
			const next = {}
			this.surveyQuestions.forEach((block) => {
				next[block.survey_question_id] = this.deriveInitialAnswer(block)
			})
			this.answers = next
		},
		isLastOdd(idx, length) {
			return length % 2 === 1 && idx === length - 1
		},
		autoResize(e) {
			const t = e.target
			t.style.height = '0px'
			t.style.height = `${t.scrollHeight}px`
		},
	},
}
</script>

<style lang="scss">
.survey-form-classic {
	max-width: 555px;
	font-family: 'Inter', sans-serif;
	display: flex;
	gap: 12px;
	min-height: 796px;
	height: 100%;
	flex-direction: column;
	justify-content: space-between;
	padding-bottom: 20px;
	&__button-wrapper {
		display: flex;
		gap: 12px;
	}
	&__back-button {
		width: 176px;
		height: 46px;
		display: flex;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		justify-content: center;
		align-items: center;
		border-radius: 8px;
		padding: 12px;
		border: 1px solid var(--button-secondary-background-color);
		color: var(--button-secondary-background-color);
		&__enable {
			color: #9b9b9b;
			border: 1px solid #9b9b9b;
		}
	}

	&__submit-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		border-radius: 8px;
		padding: 12px;
		color: #fff;
		height: 46px;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		transition: background-color 0.15s ease, opacity 0.15s ease;

		&--disabled {
			background: #9b9b9b;
			color: #fff;
			cursor: not-allowed;
			opacity: 0.9;
		}

		&--enabled {
			background: var(--button-main-background-color);
			color: var(--button-main-color);
			cursor: pointer;

			&:hover {
				opacity: 0.95;
			}
			&:active {
				opacity: 0.9;
			}
		}

		&:disabled {
			pointer-events: none;
		}
	}

	&__block {
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
		background-color: #ffffff;
	}

	&__block.has-error {
		box-shadow: 0 0 0 2px #ffb3b3 inset;
		border-color: #ff7a7a;
	}

	&__form {
		display: flex;
		height: 100%;
		flex-direction: column;
		justify-content: center;
		gap: 8px;
	}

	&__grid {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	&__grid-item {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		padding: 12px 12px 13px 12px;
		border-radius: 10px;

		&.survey-form-classic__checkbox__selected {
			box-shadow: 0 0 0 1px #e2e2e2;
		}
		&.full-span {
			grid-column: 1 / -1;
			background-color: #f3f3f6;
		}
	}

	&__title {
		display: flex;
		justify-content: flex-start;
		font-weight: 700;
		font-size: 16px;
		line-height: 22px;
		letter-spacing: 0;
		text-align: center;
		color: var(--username-category-title);
		margin-bottom: 12px;

		&__required-asterisk {
			color: #ff0000;
			font-size: 18px;
			font-weight: 700;
			margin-left: 2px;
		}
	}

	&__label {
		flex: 1 1 0;
		min-width: 0;
		overflow-wrap: anywhere;
		font-weight: 500;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		color: var(--username-category-title);
	}

	&__radio {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		padding: 12px 12px 13px 12px;
		background: #f3f3f6;
		gap: 8px;
		border-radius: 10px;
		margin-top: 8px;
	}

	&__radio__selected {
		box-shadow: 0 0 0 1px #e2e2e2;
	}

	&__checkbox {
		display: flex;
		align-items: baseline;
		padding: 12px 12px 13px 12px;
		background: #fff;
		gap: 8px;
		border-radius: 10px;
		margin-bottom: 8px;
	}

	&__checkbox__selected {
		box-shadow: 0 0 0 1px #e2e2e2;
	}

	&__custom-answer {
		display: flex;
		flex-direction: column;
		border-radius: 10px;
		padding: 12px 12px 13px 12px;
		align-items: baseline;
		gap: 8px;

		&--text-box {
			background-color: #f3f3f6;
			min-height: 134px;
			textarea {
				min-height: 110px;
			}
		}

		&--inside {
			padding: 0;
			background: transparent;
			border-radius: 0;
			gap: 4px;
			width: 100%;
			margin-top: 6px;
		}

		&__input {
			width: 100%;
			min-height: 24px;
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			letter-spacing: 0;
			padding: 0;
			height: auto;
			border: none;
			outline: none;
			background-color: #f3f3f6;
			overflow: hidden;
			resize: none;
			overflow-wrap: break-word;
			white-space: pre-wrap;
			font-family: inherit;

			&::placeholder {
				font-weight: 400;
				font-size: 18px;
				line-height: 140%;
				letter-spacing: 0;
				color: #9b9b9b;
			}
		}
	}

	@media (max-width: 480px) {
		&__grid {
			grid-template-columns: 1fr;
		}
	}
}
</style>
